import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Leaf } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/apiError';
import { EmailPasswordForm } from '../components/auth/EmailPasswordForm';
import { GoogleSignInButton } from '../components/auth/GoogleSignInButton';

const Login = () => {
  const { loginWithGoogle, loginWithPassword, registerWithPassword } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');

  const handleSubmit = async ({ email, password, name }) => {
    try {
      if (mode === 'register') {
        await registerWithPassword(email, password, name);
        toast.success('Account created. Welcome aboard!');
      } else {
        await loginWithPassword(email, password);
        toast.success('Welcome back!');
      }
      navigate('/dashboard', { replace: true });
    } catch (error) {
      // Surfaced inline by the form
      throw new Error(formatApiError(error));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 login-bg">
      <div className="absolute inset-0 bg-black/40" />

      <Card className="w-full max-w-md relative z-10 shadow-2xl border-0 my-8" data-testid="login-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-[#2E4632] rounded-full flex items-center justify-center mb-4">
            <Leaf className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-3xl font-bold text-[#2E4632] flex items-center justify-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            FarmWorker
            <span
              data-testid="beta-badge-login"
              className="text-[10px] font-semibold tracking-wide text-[#2E4632] bg-[#2E4632]/10 border border-[#2E4632]/25 rounded-full px-2 py-0.5 align-middle"
            >
              BETA
            </span>
          </CardTitle>
          <CardDescription className="text-[#5C5C5C] mt-2">
            Manage your farm workforce efficiently
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-4">
          <Tabs value={mode} onValueChange={setMode}>
            <TabsList className="grid grid-cols-2 w-full bg-[#E8E8E3] h-11 mb-5" data-testid="auth-mode-tabs">
              <TabsTrigger
                value="login"
                data-testid="tab-signin"
                className="h-9 data-[state=active]:bg-[#2E4632] data-[state=active]:text-white font-semibold"
              >
                Sign in
              </TabsTrigger>
              <TabsTrigger
                value="register"
                data-testid="tab-register"
                className="h-9 data-[state=active]:bg-[#2E4632] data-[state=active]:text-white font-semibold"
              >
                Create account
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <EmailPasswordForm key={mode} mode={mode} onSubmit={handleSubmit} />

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-[#D1D1CD]" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-xs uppercase tracking-wide text-[#5C5C5C]">
                or
              </span>
            </div>
          </div>

          <GoogleSignInButton onClick={loginWithGoogle} />

          <p className="text-xs text-center text-[#5C5C5C] mt-3">
            Signed up with Google before? Use &ldquo;Continue with Google&rdquo;.
          </p>

          <p className="text-xs text-center text-[#5C5C5C] mt-4">
            By continuing you agree to fair use of the platform.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
